import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { apiService } from '../../services/api'
import { useAuth } from '../../context/AuthContext'

// ── SVG chart primitives ────────────────────────────────────────────────────

function smoothPath(points) {
  if (points.length < 2) return ''
  let d = `M ${points[0][0]} ${points[0][1]}`
  for (let i = 1; i < points.length; i++) {
    const [x0, y0] = points[i - 1]
    const [x1, y1] = points[i]
    const cx = (x0 + x1) / 2
    d += ` C ${cx} ${y0}, ${cx} ${y1}, ${x1} ${y1}`
  }
  return d
}

function AreaChart({ data, height = 44, color = 'var(--accent)', fillId = 'areaFill', strokeWidth = 1.5, padding = 4 }) {
  if (!data || data.length < 2) return null
  const w = 100, h = height
  const max = Math.max(...data), min = Math.min(...data)
  const range = max - min || 1
  const points = data.map((v, i) => [
    padding + (i * (w - padding * 2)) / (data.length - 1),
    padding + (1 - (v - min) / range) * (h - padding * 2),
  ])
  const d = smoothPath(points)
  const area = `${d} L ${points[points.length - 1][0]} ${h - padding} L ${points[0][0]} ${h - padding} Z`
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height }}>
      <defs>
        <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${fillId})`} />
      <path d={d} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

function MultiAreaChart({ series, height = 230 }) {
  const w = 100, h = height, padding = 10
  const allMax = Math.max(...series.flatMap(s => s.data))
  const allMin = 0
  const range = allMax - allMin || 1
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height }}>
      <defs>
        {series.map((s, i) => (
          <linearGradient key={i} id={`gradS${i}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={s.color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={s.color} stopOpacity="0" />
          </linearGradient>
        ))}
      </defs>
      {[0.25, 0.5, 0.75].map((g, i) => (
        <line key={i} x1="0" x2={w}
          y1={padding + g * (h - padding * 2)}
          y2={padding + g * (h - padding * 2)}
          stroke="var(--border)" strokeDasharray="1.5 3" strokeWidth="0.4" />
      ))}
      {series.map((s, sIdx) => {
        const points = s.data.map((v, i) => [
          padding + (i * (w - padding * 2)) / (s.data.length - 1),
          padding + (1 - (v - allMin) / range) * (h - padding * 2),
        ])
        const d = smoothPath(points)
        const area = `${d} L ${points[points.length - 1][0]} ${h - padding} L ${points[0][0]} ${h - padding} Z`
        return (
          <g key={sIdx}>
            <path d={area} fill={`url(#gradS${sIdx})`} opacity="0.8" />
            <path d={d} fill="none" stroke={s.color} strokeWidth="1.4" vectorEffect="non-scaling-stroke" strokeLinecap="round" />
          </g>
        )
      })}
    </svg>
  )
}

function BarChart({ data, height = 70, color = 'rgba(124,92,255,0.45)' }) {
  if (!data || data.length === 0) return null
  const max = Math.max(...data) || 1
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height, width: '100%' }}>
      {data.map((v, i) => (
        <div key={i} style={{
          flex: 1,
          height: `${(v / max) * 100}%`,
          background: i === data.length - 1 ? 'var(--accent)' : color,
          borderRadius: 2,
          transition: 'height 0.4s ease',
        }} />
      ))}
    </div>
  )
}

function DonutChart({ data, size = 130, thickness = 16, centerLabel, centerValue }) {
  const total = data.reduce((s, d) => s + d.value, 0)
  const r = (size - thickness) / 2
  const c = 2 * Math.PI * r
  let offset = 0
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--border)" strokeWidth={thickness} />
        {data.map((d, i) => {
          const len = (d.value / total) * c
          const dasharray = `${len} ${c - len}`
          const seg = (
            <circle key={i} cx={size / 2} cy={size / 2} r={r}
              fill="none" stroke={d.color}
              strokeWidth={thickness}
              strokeDasharray={dasharray}
              strokeDashoffset={-offset}
              strokeLinecap="butt" />
          )
          offset += len + 2
          return seg
        })}
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)' }}>{centerValue}</div>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{centerLabel}</div>
      </div>
    </div>
  )
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const timeAgo = (dateStr) => {
  if (!dateStr) return ''
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

const PRIORITY_BADGE = {
  urgent: 'badge-danger', high: 'badge-danger',
  medium: 'badge-warning', normal: 'badge-warning', low: 'badge-info',
}

const PLAN_COLORS = ['#7C5CFF', '#5B7CFF', '#2BD9A6', '#FFB454']

function getActivityText(a) {
  const user = a.user_name || a.user || 'Someone'
  const site = a.site_name || a.site || 'a site'
  const client = a.company_name || a.client || ''
  if (a.type === 'site_published') return <><strong>{user}</strong> published <strong>{site}</strong>{client ? ` for ${client}` : ''}</>
  if (a.type === 'client_registered') return <>New client <strong>{client || user}</strong> registered</>
  if (a.type === 'request_submitted') return <><strong>{user}</strong> submitted a request for <strong>{site}</strong></>
  return <><strong>{user}</strong> {a.description || a.action || 'edited'} <strong>{site}</strong>{client ? ` (${client})` : ''}</>
}

// ── SVG Icons ────────────────────────────────────────────────────────────────

const ICONS = {
  card: <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><path d="M1 10h22"/></svg>,
  globe: <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M3.6 9h16.8M3.6 15h16.8M12 3c-2.5 3-4 5.8-4 9s1.5 6 4 9M12 3c2.5 3 4 5.8 4 9s-1.5 6-4 9"/></svg>,
  eye: <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  inbox: <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z"/></svg>,
  arrowUp: <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6"/></svg>,
  arrowRight: <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>,
  upload: <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  plus: <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>,
  rocket: <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m3.5 20.5 3-3"/><path d="M15 7c2.5-2.5 3-6 3-6S14.5 1.5 12 4l-8 8 3 3 8-2z"/><path d="m7 17 3-3"/></svg>,
  edit: <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4v16h16v-7"/><path d="m14.5 5.5 4 4L11 17l-4 1 1-4 7.5-8.5Z"/></svg>,
  message: <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  cloud: <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></svg>,
  check: <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  x: <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>,
}

const ACT_ICON = {
  publish: 'rocket', site_published: 'rocket',
  edit: 'edit', comment: 'message', request_submitted: 'message',
  billing: 'card', deploy: 'cloud', request: 'inbox', domain: 'globe', backup: 'inbox',
  client_registered: 'inbox', new_page: 'edit', default: 'edit',
}
const ACT_TONE = {
  publish: 'success', site_published: 'success',
  edit: 'info', new_page: 'info',
  comment: 'warning', request_submitted: 'warning',
  billing: 'success', client_registered: 'success',
  deploy: 'info', request: 'warning', domain: 'info',
}

// ── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value, sub, delta, deltaText, series, color, icon }) {
  return (
    <div className="card card-pad card-glow" style={{ position: 'relative', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <span className="icon-tile" style={{ background: `${color}1F`, color, borderColor: 'transparent' }}>
          {ICONS[icon]}
        </span>
        <span className={`badge ${delta === 'up' ? 'badge-success' : 'badge-danger'}`}
          style={{ background: delta === 'up' ? 'var(--success-soft)' : 'var(--danger-soft)' }}>
          <span style={{ display: 'inline-flex', transform: delta !== 'up' ? 'rotate(180deg)' : undefined }}>{ICONS.arrowUp}</span>
          {deltaText || '—'}
        </span>
      </div>
      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.025em', marginTop: 4, fontFamily: 'var(--font-mono)' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>
      <div style={{ margin: '10px -22px -22px', height: 44 }}>
        {series && series.length > 1 && (
          <AreaChart data={series} height={44} color={color} fillId={`grad-${label.replace(/\s/g, '')}`} strokeWidth={1.5} padding={4} />
        )}
      </div>
    </div>
  )
}

function Legend({ color, label, value, delta, up }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-muted)' }}>
        <span style={{ width: 8, height: 8, borderRadius: 3, background: color, flexShrink: 0 }} />
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)' }}>{value}</span>
        <span style={{ fontSize: 11.5, color: up ? 'var(--success)' : 'var(--danger)', display: 'flex', alignItems: 'center', gap: 2 }}>
          <span style={{ display: 'inline-flex', transform: up ? undefined : 'rotate(180deg)' }}>{ICONS.arrowUp}</span>
          {delta}
        </span>
      </div>
    </div>
  )
}

function ActivityRow({ a }) {
  const type = a.type || a.edit_type || 'default'
  const tone = ACT_TONE[type] || ''
  const iconName = ACT_ICON[type] || ACT_ICON.default
  return (
    <div className="activity-row">
      <div className="activity-tail" />
      <span className={`icon-tile ${tone}`} style={{ width: 30, height: 30, fontSize: 13, position: 'relative', zIndex: 1, flexShrink: 0 }}>
        {ICONS[iconName]}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: 'var(--text-soft)' }}>
          {getActivityText(a)}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
          {timeAgo(a.created_at || a.timestamp)}
        </div>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
        {timeAgo(a.created_at || a.timestamp)}
      </div>
    </div>
  )
}

function RequestRow({ req }) {
  const badgeClass = PRIORITY_BADGE[req.priority] || 'badge-warning'
  return (
    <Link to={`/admin/requests?open=${req.id}`} style={{ textDecoration: 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
        <div className={`avatar avatar-grad-${(req.id?.charCodeAt(0) % 6) + 1}`} style={{ width: 30, height: 30, fontSize: 11 }}>
          {(req.company_name || req.title || '?').slice(0, 2).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, color: 'var(--text)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {req.title}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
            {req.company_name} · {timeAgo(req.created_at)}
          </div>
        </div>
        <span className={`badge ${badgeClass}`} style={{ fontSize: 10 }}>{req.priority || 'normal'}</span>
      </div>
    </Link>
  )
}

function DeployRow({ d }) {
  const tone = d.status === 'success' ? 'success' : d.status === 'failed' ? 'danger' : 'warning'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span className={`icon-tile ${tone}`} style={{ width: 26, height: 26, fontSize: 12, flexShrink: 0 }}>
        {d.status === 'success' ? ICONS.check : d.status === 'failed' ? ICONS.x : ICONS.cloud}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, color: 'var(--text)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {d.site}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1, fontFamily: 'var(--font-mono)' }}>
          {d.env}{d.client ? ` · ${d.client}` : ''}
        </div>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>{d.time}</div>
    </div>
  )
}


function TopSiteRow({ s, rank, max }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ width: 18, fontSize: 11, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
        {rank.toString().padStart(2, '0')}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, color: 'var(--text)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {s.name || s.site_name}
        </div>
        <div className="progress thin" style={{ marginTop: 6 }}>
          <div style={{ width: `${((s.traffic || s.page_views || 1) / max) * 100}%` }} />
        </div>
      </div>
      <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-soft)', flexShrink: 0 }}>
        {(s.traffic || s.page_views || 0).toLocaleString()}
      </div>
    </div>
  )
}

// ── Main component ───────────────────────────────────────────────────────────

const PERIODS = ['Today', '30 days', '90 days', 'YTD']
const PERIOD_KEYS = { 'Today': '7d', '30 days': '30d', '90 days': '90d', 'YTD': '90d' }
const CHART_TABS = ['All', 'Edits', 'Deploys', 'Views']

export default function AdminOverview() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [activePeriod, setActivePeriod] = useState('30 days')
  const [chartTab, setChartTab] = useState('All')
  const selectedPeriod = PERIOD_KEYS[activePeriod]

  const greeting = (() => {
    const h = new Date().getHours()
    if (h < 12) return 'Good morning'
    if (h < 18) return 'Good afternoon'
    return 'Good evening'
  })()

  const { data: stats, isLoading: statsLoading } = useQuery(
    ['admin-stats', selectedPeriod],
    () => apiService.get(`/users/admin/dashboard-stats?period=${selectedPeriod}`).then(r => r.data),
    { staleTime: 300000, retry: 2 }
  )

  const { data: activities, isLoading: activitiesLoading } = useQuery(
    ['admin-activities', selectedPeriod],
    () => {
      const daysMap = { '7d': 7, '30d': 30, '90d': 90 }
      const days = daysMap[selectedPeriod] || 30
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - days)
      return apiService.get(`/edit-logs?limit=7&startDate=${startDate.toISOString()}`).then(r => r.data.logs || [])
    },
    { staleTime: 300000, retry: 1 }
  )

  const { data: quickStats, isLoading: quickStatsLoading } = useQuery(
    'admin-quick-stats',
    () => apiService.get('/users/admin/quick-stats').then(r => r.data),
    { staleTime: 300000, retry: 1 }
  )

  const { data: plansRaw } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(r => r.data),
    { staleTime: 10 * 60 * 1000 }
  )

  const s = stats || {}
  const growth = s.growth || {}
  const pendingRequests = quickStats?.pendingRequestsDetail || []

  // Real monthly series (6 buckets) straight from the backend
  const revenueData = (s.revenueData || []).map(d => d.revenue ?? 0)
  const viewsData = (s.pageViewsData || []).map(d => d.views ?? 0)
  const editsData = (s.editsData || []).map(d => d.edits ?? 0)
  const deploysData = (s.deploysData || []).map(d => d.deploys ?? 0)
  const traffic24h = s.traffic24h || []
  const total24h = traffic24h.reduce((a, b) => a + b, 0)

  const revSeries = revenueData.length > 1 ? revenueData : null
  const sitesSeries = deploysData.length > 1 ? deploysData : null
  const viewsSeries = viewsData.length > 1 ? viewsData : null

  // Formatting helpers for real values + growth deltas
  const fmtK = (n) => ((n ?? 0) >= 1000 ? `${((n ?? 0) / 1000).toFixed(1)}k` : `${n ?? 0}`)
  const fmtPct = (g) => `${(g ?? 0) >= 0 ? '+' : ''}${g ?? 0}%`
  const dir = (g) => ((g ?? 0) >= 0 ? 'up' : 'down')

  // Plan distribution — name + price resolved from the billing plans cache
  const planDist = (quickStats?.planDistribution || []).map((p, i) => {
    const meta = plansRaw?.[p.plan] || {}
    const name = meta.name || (p.plan ? p.plan.charAt(0).toUpperCase() + p.plan.slice(1) : 'Unknown')
    return { name, count: p.count, price: meta.price, color: PLAN_COLORS[i % PLAN_COLORS.length] }
  })

  const mrr = s.monthlyRevenue ?? 0
  const mrrDisplay = mrr >= 1000 ? `£${(mrr / 1000).toFixed(1)}k` : `£${mrr}`

  // Recent deploys — real published sites from quick-stats
  const recentDeploys = (quickStats?.recentDeploys || []).map(d => ({
    id: d.id,
    site: d.name || 'Untitled site',
    env: d.assigned_domain || d.custom_domain || 'production',
    client: d.company_name,
    status: 'success',
    time: timeAgo(d.published_at),
  }))

  // Top sites from quickStats if available, otherwise empty
  const topSites = (quickStats?.topSites || [])
  const topSitesMax = topSites.length > 0 ? Math.max(...topSites.map(s => s.traffic || s.page_views || 1)) : 1

  // Platform-activity chart series — filtered by the active tab
  const allChartSeries = [
    { key: 'Views', color: '#7C5CFF', data: viewsData },
    { key: 'Edits', color: '#5B7CFF', data: editsData },
    { key: 'Deploys', color: '#2BD9A6', data: deploysData },
  ]
  const chartSeries = chartTab === 'All' ? allChartSeries : allChartSeries.filter(x => x.key === chartTab)

  // Export the monthly series as CSV
  const exportCSV = () => {
    const months = s.pageViewsData || []
    const headers = ['Month', 'Page views', 'Edits', 'Deploys', 'Revenue (GBP)']
    const rows = months.map((m, i) => [m.label, viewsData[i] ?? 0, editsData[i] ?? 0, deploysData[i] ?? 0, revenueData[i] ?? 0])
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `webnest-dashboard-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Overview · WebNest</div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {greeting}, {user?.name?.split(' ')[0] || 'Admin'}
            {!statsLoading && s.publishedSites > 0 && (
              <span className="badge badge-success">
                <span className="badge-dot" /> {s.publishedSites} sites live
              </span>
            )}
          </h1>
          <div className="subtitle">
            {statsLoading
              ? 'Loading platform data…'
              : `Here's what's moving across your ${s.totalClients ?? 0} clients today.`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="tabs">
            {PERIODS.map(p => (
              <button key={p} className={activePeriod === p ? 'active' : ''} onClick={() => setActivePeriod(p)}>
                {p}
              </button>
            ))}
          </div>
          <button className="btn btn-ghost" style={{ gap: 6 }} onClick={exportCSV}>
            {ICONS.upload} Export
          </button>
          <button className="btn btn-primary" style={{ gap: 6 }} onClick={() => navigate('/admin/sites/new')}>
            {ICONS.plus} New site
          </button>
        </div>
      </div>

      <div className="page-body">

        {/* ── Row 1: KPI stat cards ─────────────────────────────────────── */}
        <div className="bento" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          <StatCard
            label="Monthly recurring"
            value={statsLoading ? '—' : mrrDisplay}
            sub={`${s.payingClients ?? 0} paying ${s.payingClients === 1 ? 'client' : 'clients'}`}
            delta={dir(growth.revenue)}
            deltaText={fmtPct(growth.revenue)}
            series={revSeries}
            color="#A78BFA"
            icon="card"
          />
          <StatCard
            label="Active sites"
            value={statsLoading ? '—' : (s.publishedSites ?? 0)}
            sub={`${(s.totalSites ?? 0) - (s.publishedSites ?? 0)} in draft or staging`}
            delta={dir(growth.sites)}
            deltaText={fmtPct(growth.sites)}
            series={sitesSeries}
            color="#5B7CFF"
            icon="globe"
          />
          <StatCard
            label="Page views"
            value={statsLoading ? '—' : fmtK(s.totalPageViews ?? 0)}
            sub={`Across all sites · last ${activePeriod.toLowerCase()}`}
            delta={dir(growth.pageViews)}
            deltaText={fmtPct(growth.pageViews)}
            series={viewsSeries}
            color="#2BD9A6"
            icon="eye"
          />
          <StatCard
            label="Open requests"
            value={statsLoading ? '—' : (s.activeRequests ?? 0)}
            sub={`${s.urgentRequests ?? 0} marked high priority`}
            delta={dir(growth.requests)}
            deltaText={fmtPct(growth.requests)}
            color="#FFB454"
            icon="inbox"
          />
        </div>

        {/* ── Row 2: Activity chart + Plan mix ─────────────────────────── */}
        <div className="bento" style={{ gridTemplateColumns: '2fr 1fr', gap: 14, alignItems: 'stretch' }}>

          {/* Platform activity chart */}
          <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column', minHeight: 340 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <div>
                <h2 className="h2">Platform activity</h2>
                <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>
                  Edits, deploys and page views — last 6 months
                </div>
              </div>
              <div className="tabs">
                {CHART_TABS.map(t => (
                  <button key={t} className={chartTab === t ? 'active' : ''} onClick={() => setChartTab(t)}>{t}</button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 24, marginBottom: 14 }}>
              <Legend color="#7C5CFF" label="Page views" value={fmtK(s.totalPageViews ?? 0)} delta={fmtPct(growth.pageViews)} up={dir(growth.pageViews) === 'up'} />
              <Legend color="#5B7CFF" label="Edits" value={(s.totalEdits ?? 0).toLocaleString()} delta={fmtPct(growth.edits)} up={dir(growth.edits) === 'up'} />
              <Legend color="#2BD9A6" label="Deploys" value={s.publishedSites ?? 0} delta={fmtPct(growth.sites)} up={dir(growth.sites) === 'up'} />
            </div>

            <div style={{ flex: 1, position: 'relative' }}>
              {viewsData.length > 1 ? (
                <>
                  <MultiAreaChart height={230} series={chartSeries} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-faint)', marginTop: 6, fontFamily: 'var(--font-mono)' }}>
                    {(s.pageViewsData || []).map((d, i) => <span key={i}>{d.label}</span>)}
                  </div>
                </>
              ) : (
                <div style={{ height: 230, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-faint)', fontSize: 13 }}>
                  No activity data yet
                </div>
              )}
            </div>
          </div>

          {/* Plan distribution */}
          <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div>
                <h2 className="h2">Plan mix</h2>
                <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>Subscriptions across all clients</div>
              </div>
            </div>
            {planDist.length > 0 ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18 }}>
                  <DonutChart
                    size={130}
                    thickness={16}
                    data={planDist.map(p => ({ value: p.count || 1, color: p.color }))}
                    centerLabel="active"
                    centerValue={planDist.reduce((acc, p) => acc + (p.count || 0), 0)}
                  />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {planDist.map((p) => (
                      <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 3, background: p.color, flexShrink: 0 }} />
                        <span style={{ color: 'var(--text-soft)', flex: 1 }}>{p.name}</span>
                        <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>{p.count}</span>
                        {p.price && <span style={{ color: 'var(--text-faint)', fontSize: 11, fontFamily: 'var(--font-mono)' }}>£{p.price}</span>}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="divider" />
                <div style={{ marginTop: 14 }}>
                  <div className="eyebrow" style={{ marginBottom: 8 }}>Forecast</div>
                  <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)' }}>
                    {mrrDisplay}<span style={{ fontSize: 13, color: 'var(--text-muted)', fontFamily: 'var(--font)' }}> /mo</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    From {s.payingClients ?? 0} paying {s.payingClients === 1 ? 'client' : 'clients'}
                  </div>
                </div>
              </>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-faint)', fontSize: 13 }}>
                {quickStatsLoading ? 'Loading…' : 'No plan data yet'}
              </div>
            )}
          </div>
        </div>

        {/* ── Row 3: Activity feed + Open requests + Recent deploys ─────── */}
        <div className="bento" style={{ gridTemplateColumns: '1.4fr 1fr 1fr', gap: 14, alignItems: 'stretch' }}>

          {/* Live activity */}
          <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div>
                <h2 className="h2">Live activity</h2>
                <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className="dot-live" style={{ width: 6, height: 6 }} /> Streaming
                </div>
              </div>
              <Link to="/admin/edit-logs">
                <button className="btn btn-sm btn-ghost" style={{ gap: 4 }}>View all {ICONS.arrowRight}</button>
              </Link>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {activitiesLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="activity-row" style={{ opacity: 0.4 }}>
                    <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--border)', flexShrink: 0 }} />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ height: 12, borderRadius: 4, background: 'var(--border)', width: '80%' }} />
                      <div style={{ height: 10, borderRadius: 4, background: 'var(--border)', width: '50%' }} />
                    </div>
                  </div>
                ))
              ) : activities && activities.length > 0 ? (
                activities.slice(0, 7).map((a, i) => <ActivityRow key={a.id || i} a={a} />)
              ) : (
                <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-faint)', fontSize: 13 }}>
                  No activity yet
                </div>
              )}
            </div>
          </div>

          {/* Open requests */}
          <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div>
                <h2 className="h2">Inbox</h2>
                <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>
                  {s.activeRequests ?? 0} open · {s.urgentRequests ?? 0} high
                </div>
              </div>
              <Link to="/admin/requests">
                <button className="btn btn-sm btn-ghost">{ICONS.arrowRight}</button>
              </Link>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {quickStatsLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--border)', flexShrink: 0 }} />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ height: 12, borderRadius: 4, background: 'var(--border)', width: '75%' }} />
                      <div style={{ height: 10, borderRadius: 4, background: 'var(--border)', width: '50%' }} />
                    </div>
                  </div>
                ))
              ) : pendingRequests.length > 0 ? (
                pendingRequests.map(req => <RequestRow key={req.id} req={req} />)
              ) : (
                <div style={{ padding: '24px 0', display: 'flex', alignItems: 'center', gap: 8, color: 'var(--success)', fontSize: 13 }}>
                  {ICONS.check} All caught up
                </div>
              )}
            </div>
          </div>

          {/* Recent deploys */}
          <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div>
                <h2 className="h2">Recent deploys</h2>
                <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>
                  {recentDeploys.length > 0 ? `${recentDeploys.length} of ${recentDeploys.length} successful` : '—'}
                </div>
              </div>
              <Link to="/admin/sites">
                <button className="btn btn-sm btn-ghost">{ICONS.arrowRight}</button>
              </Link>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {quickStatsLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <div style={{ width: 26, height: 26, borderRadius: 8, background: 'var(--border)', flexShrink: 0 }} />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
                      <div style={{ height: 12, borderRadius: 4, background: 'var(--border)', width: '70%' }} />
                      <div style={{ height: 10, borderRadius: 4, background: 'var(--border)', width: '55%' }} />
                    </div>
                  </div>
                ))
              ) : recentDeploys.length > 0 ? (
                recentDeploys.map(d => <DeployRow key={d.id} d={d} />)
              ) : (
                <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-faint)', fontSize: 13 }}>
                  No deploys yet
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Row 4: Traffic 24h + Top sites (full width) ──────────────── */}
        <div className="bento" style={{ gridTemplateColumns: '1fr 1.8fr', gap: 14, alignItems: 'stretch' }}>

          {/* Traffic last 24h */}
          <div className="card card-pad">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <h2 className="h2">Traffic — last 24h</h2>
              <span className="badge">{ICONS.eye} Live</span>
            </div>
            <div className="text-muted" style={{ fontSize: 12.5, marginBottom: 18 }}>Across all client sites</div>
            <div className="stat-row">
              <div className="num">{statsLoading ? '—' : total24h.toLocaleString()}</div>
              <div className="text-muted" style={{ fontSize: 12 }}>visits</div>
            </div>
            <div style={{ marginTop: 16 }}>
              {total24h > 0 ? (
                <>
                  <BarChart data={traffic24h} height={70} color="rgba(124,92,255,0.45)" />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--text-faint)', marginTop: 8, fontFamily: 'var(--font-mono)' }}>
                    <span>24h ago</span><span>18h</span><span>12h</span><span>6h</span><span>now</span>
                  </div>
                </>
              ) : (
                <div style={{ height: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-faint)', fontSize: 12.5 }}>
                  No visits in the last 24h
                </div>
              )}
            </div>
          </div>

          {/* Top sites this month — expanded */}
          <div className="card card-pad">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div>
                <h2 className="h2">Top sites this month</h2>
                <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>Ranked by page views</div>
              </div>
              <Link to="/admin/sites">
                <button className="btn btn-sm btn-ghost" style={{ gap: 4 }}>All sites {ICONS.arrowRight}</button>
              </Link>
            </div>
            {topSites.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {topSites.slice(0, 8).map((site, i) => (
                  <TopSiteRow key={site.id || i} s={site} rank={i + 1} max={topSitesMax} />
                ))}
              </div>
            ) : (
              <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-faint)', fontSize: 13 }}>
                Analytics data will appear here once sites receive traffic.
              </div>
            )}
          </div>
        </div>

      </div>
    </>
  )
}
